import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import SectionTitle from '@/components/base/SectionTitle';
import { hotKeywords, mediaList } from '@/mocks/media';
import { useSeo } from '@/hooks/useSeo';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [input, setInput] = useState(q);
  const [loading, setLoading] = useState(false);

  useSeo({
    title: q ? `“${q}”的搜索结果 - 看片影视大全` : '搜索影视 - 看片影视大全',
    description: q
      ? `看片影视大全中关于“${q}”的搜索结果，为你找到相关的电影、电视剧与综艺节目。`
      : '在看片影视大全中搜索你感兴趣的电影、电视剧与综艺节目。',
    keywords: `看片,影视搜索,${q || '影视大全'}`,
  });

  useEffect(() => {
    setInput(q);
    if (!q) return;
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, [q]);

  const results = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    if (!keyword) return [];
    return mediaList.filter((item) => {
      const haystack = [
        item.title,
        item.region,
        item.director,
        ...item.genres,
        ...item.actors,
        item.description,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [q]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    if (!next) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: next });
  };

  const hotPicks = [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, 5);

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900 font-medium">搜索结果</span>
      </nav>

      <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950 tracking-tight mb-5">
        {q ? <>“{q}”的搜索结果</> : '搜索影视内容'}
      </h1>

      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6 max-w-2xl">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入影视名称、类型或演员"
            className="w-full pl-11 pr-4 py-3 text-sm rounded-md bg-background-100 border border-background-300/70 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
        </div>
        <button
          type="submit"
          className="px-6 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          搜索
        </button>
      </form>

      {!q ? (
        <div>
          <p className="text-sm text-foreground-500 mb-4">输入关键词开始搜索，或试试这些热门关键字：</p>
          <div className="flex flex-wrap gap-2 mb-12">
            {hotKeywords.map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => setSearchParams({ q: kw })}
                className="px-4 py-2 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-600 cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
              >
                {kw}
              </button>
            ))}
          </div>
          <SectionTitle title="热门推荐" subtitle="高热度影视不容错过" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {hotPicks.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="w-full aspect-[2/3] rounded-lg bg-background-200"></div>
              <div className="mt-2.5 h-4 w-3/4 rounded bg-background-200"></div>
              <div className="mt-2 h-3 w-1/2 rounded bg-background-200"></div>
            </div>
          ))}
        </div>
      ) : results.length === 0 ? (
        <div>
          <div className="flex flex-col items-center justify-center py-14 px-6 text-center bg-background-100 rounded-lg border border-background-200/70 mb-12">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
              <i className="ri-search-eye-line text-3xl"></i>
            </div>
            <h3 className="text-base font-semibold text-foreground-900">没有找到与“{q}”相关的内容</h3>
            <p className="mt-2 text-sm text-foreground-500 max-w-md">
              换个关键词试试，或浏览下面的热门推荐影视。
            </p>
          </div>
          <SectionTitle title="热门推荐" subtitle="或许这里有你感兴趣的" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {hotPicks.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      ) : (
        <div>
          <p className="text-sm text-foreground-500 mb-5">
            共找到 <span className="font-semibold text-primary-600">{results.length}</span> 部相关影视
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
            {results.map((item) => (
              <MovieCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}